package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.ui.draw.blur
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.decodeToImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.data.PickedMediaHolder
import org.gymly.model.AdminPromotion
import org.gymly.model.AdminPromotionUpsert
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import org.gymly.ui.components.AdminDateField
import org.gymly.ui.components.FileUploadIconButton
import org.gymly.theme.GymlySpace
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.openPromoMediaPicker
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.userMessage
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn

private enum class MarketingDateFilter(val label: String, val days: Int?) {
    Days7("7 días", 7),
    Days30("30 días", 30),
    All("Todos", null),
}

private sealed class MarketingDialog {
    data object Create : MarketingDialog()
    data class Edit(val item: AdminPromotion) : MarketingDialog()
    data class Detail(val item: AdminPromotion) : MarketingDialog()
    data class Delete(val item: AdminPromotion) : MarketingDialog()
}

@Composable
fun AdminMarketingScreen() {
    val scope = rememberCoroutineScope()
    var items by remember { mutableStateOf<List<AdminPromotion>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(MarketingDateFilter.All) }
    var dialog by remember { mutableStateOf<MarketingDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    val pendingApprovals = LocalPendingApprovalNotifier.current

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                items = AdminRepo.api.listPromotions()
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar las promociones.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(items, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        items.filter { p ->
            val matchesQuery = q.isEmpty() ||
                p.title.lowercase().contains(q) ||
                p.dateRangeLabel().lowercase().contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = p.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "title",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "active" -> { p -> if (p.active) 1 else 0 }
                else -> { p -> p.title.lowercase() }
            }
        }
    }

    val nowIso = Clock.System.now().toString().take(7) // YYYY-MM
    val total = filtered.size
    val active = filtered.count { it.active }
    val inactive = filtered.count { !it.active }
    val endingThisMonth = filtered.count { p ->
        val end = p.endsAt?.take(7)
        end != null && end == nowIso
    }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        Column(Modifier.fillMaxSize()) {
            MarketingHeader(dateFilter) { dateFilter = it }
            Spacer(Modifier.height(16.dp))
            MarketingKpiRow(total, active, inactive, endingThisMonth, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            MarketingDirectoryCard(
                promotions = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = MarketingDialog.Create },
                onView = { dialog = MarketingDialog.Detail(it) },
                onEdit = { dialog = MarketingDialog.Edit(it) },
                onDelete = { dialog = MarketingDialog.Delete(it) },
            )
        }
    }

    when (val d = dialog) {
        is MarketingDialog.Detail -> PromotionDetailDialog(d.item) { dialog = null }
        is MarketingDialog.Create -> PromotionFormDialog(
            title = "Agregar promoción",
            initial = null,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createPromotion(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage("No se pudo crear la promoción.")
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is MarketingDialog.Edit -> PromotionFormDialog(
            title = "Editar promoción",
            initial = d.item,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updatePromotion(d.item.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage("No se pudo guardar la promoción.")
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is MarketingDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar promoción") },
            text = {
                Text("¿Seguro que deseas eliminar «${d.item.title}»? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deletePromotion(d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) {
                                dialog = null
                                error = null
                            } else {
                                error = e.userMessage("No se pudo eliminar la promoción.")
                            }
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun MarketingHeader(
    dateFilter: MarketingDateFilter,
    onFilterChange: (MarketingDateFilter) -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Marketing", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Carrusel de la app / web móvil y flyers programados por WhatsApp. Elige el canal al crear cada pieza.",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        Box {
            OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                Text(dateFilter.label, color = AdminColors.Text)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                MarketingDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(opt.label) },
                        onClick = {
                            onFilterChange(opt)
                            menuOpen = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun MarketingKpiRow(
    total: Int,
    active: Int,
    inactive: Int,
    endingThisMonth: Int,
    compact: Boolean,
) {
    val kpiItems = listOf(
        Triple("Total de promociones", total.toString(), AdminColors.Primary),
        Triple("Activas", active.toString(), Color(0xFF2E7D32)),
        Triple("Inactivas", inactive.toString(), Color(0xFF757575)),
        Triple("Terminan este mes", endingThisMonth.toString(), Color(0xFFF59E0B)),
    )
    if (compact) {
        // Celular: franja de indicadores de una línea.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            kpiItems.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            kpiItems.forEach { (label, value, accent) ->
                MarketingKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun MarketingKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.MarketingDirectoryCard(
    promotions: List<AdminPromotion>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminPromotion) -> Unit,
    onEdit: (AdminPromotion) -> Unit,
    onDelete: (AdminPromotion) -> Unit,
) {
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text("Promociones", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                SearchField(query, onQueryChange)
                if (canWriteModule(AdminModuleAccess.MARKETING)) Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("+ Nueva promoción", fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Promociones", fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) { SearchField(query, onQueryChange) }
                    if (canWriteModule(AdminModuleAccess.MARKETING)) Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Nueva promoción", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay promociones todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    promotions.forEach { p -> PromotionMobileCard(p, onView, onEdit, onDelete) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                PromotionsTable(
                    promotions,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun SearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun PromotionsTable(
    promotions: List<AdminPromotion>,
    listPaging: AdminListPaging,
    onView: (AdminPromotion) -> Unit,
    onEdit: (AdminPromotion) -> Unit,
    onDelete: (AdminPromotion) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Promoción", "title", listPaging.sort, listPaging::onSort, Modifier.weight(1.8f))
            AdminSortableHeader("Activa", "active", listPaging.sort, listPaging::onSort, Modifier.weight(0.9f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            promotions.forEach { p ->
                val status = p.status()
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.8f), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(Color(status.dotColorArgb())),
                        )
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(p.title, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                            Text(promotionSubtitle(p), color = AdminColors.TextMuted, fontSize = 12.sp)
                        }
                    }
                    Box(Modifier.weight(0.9f)) { StatusBadge(status.labelEs(), status.badgeBgArgb(), status.badgeFgArgb()) }
                    Row(Modifier.width(140.dp)) {
                        IconButton(onClick = { onView(p) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        if (canWriteModule(AdminModuleAccess.MARKETING)) IconButton(onClick = { onEdit(p) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.Primary)
                        }
                        if (canDeleteModule(AdminModuleAccess.MARKETING)) IconButton(onClick = { onDelete(p) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun PromotionMobileCard(
    item: AdminPromotion,
    onView: (AdminPromotion) -> Unit,
    onEdit: (AdminPromotion) -> Unit,
    onDelete: (AdminPromotion) -> Unit,
) {
    val status = item.status()
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(14.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(Color(status.dotColorArgb())),
            )
            Spacer(Modifier.width(8.dp))
            Column(Modifier.weight(1f)) {
                Text(item.title, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                Text(promotionSubtitle(item), color = AdminColors.TextMuted, fontSize = 12.sp)
            }
            StatusBadge(status.labelEs(), status.badgeBgArgb(), status.badgeFgArgb())
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted)
            }
            if (canWriteModule(AdminModuleAccess.MARKETING)) IconButton(onClick = { onEdit(item) }) {
                Icon(Icons.Outlined.Edit, null, tint = AdminColors.Primary)
            }
            if (canDeleteModule(AdminModuleAccess.MARKETING)) IconButton(onClick = { onDelete(item) }) {
                Icon(Icons.Outlined.Delete, null, tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun StatusBadge(label: String, bg: Long, fg: Long) {
    Text(
        label,
        color = Color(fg),
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(999.dp))
            .background(Color(bg))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun PromotionDetailDialog(item: AdminPromotion, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        // Columna normal con scroll: VerticalScrollColumn ocupaba todo el alto y dejaba espacio vacío abajo.
        Column(
            Modifier
                .widthIn(max = 460.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Text("Detalle de la promoción", fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            val portada = item.portadaImagen()
            if (portada != null) {
                // Banner guardado: se muestra la imagen almacenada.
                StoredBannerPreview(
                    fullUrl = portada.fullUrl,
                    modifier = Modifier.fillMaxWidth(0.7f).align(Alignment.CenterHorizontally),
                )
            } else {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .height(140.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .border(1.dp, AdminColors.Outline, RoundedCornerShape(12.dp))
                        .background(AdminColors.Surface),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        when {
                            item.displayType == "VIDEO" || !item.videoUrl.isNullOrBlank() -> "Video"
                            else -> "Sin banner"
                        },
                        color = AdminColors.TextMuted,
                        fontSize = 13.sp,
                    )
                }
            }
            Spacer(Modifier.height(14.dp))
            DetailRow("Título", item.title)
            DetailRow("Canal", item.channel.labelEs())
            if (item.channel.includesApp()) {
                DetailRow("Desde (app)", item.startsAt?.take(10) ?: "—")
                DetailRow("Hasta (app)", item.endsAt?.take(10) ?: "—")
            }
            if (item.channel.includesWhatsApp()) {
                DetailRow("Envío WhatsApp", item.scheduledLabel())
                DetailRow("Estado WhatsApp", item.whatsappSendStatus ?: "PENDING")
                item.whatsappSentAt?.let { DetailRow("Enviado por WhatsApp", it.take(16).replace('T', ' ')) }
                // Motivo del último intento: si queda pendiente o fallida, aquí se ve qué falta.
                item.whatsappError?.let { DetailRow("Motivo", it) }
            }
            DetailRow("Activa", item.status().labelEs())
            Spacer(Modifier.height(16.dp))
            Button(
                onClick = onDismiss,
                modifier = Modifier.align(Alignment.End),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AdminColors.Primary,
                    contentColor = AdminColors.OnBrand,
                ),
            ) {
                Text("Cerrar")
            }
        }
    }
}

/**
 * Imagen guardada de un banner. La URL exige sesión, así que los bytes se piden con el cliente autenticado.
 * Marco 4:5, como en el portal.
 */
@Composable
private fun StoredBannerPreview(fullUrl: String, modifier: Modifier = Modifier) {
    var bitmap by remember(fullUrl) { mutableStateOf<ImageBitmap?>(null) }
    var failed by remember(fullUrl) { mutableStateOf(false) }
    LaunchedEffect(fullUrl) {
        failed = false
        bitmap = null
        val bytes = runCatching { AdminRepo.api.fetchPromotionImageBytes(fullUrl) }.getOrNull()
        bitmap = bytes?.takeIf { it.isNotEmpty() }?.let { runCatching { it.decodeToImageBitmap() }.getOrNull() }
        failed = bitmap == null
    }
    Box(
        modifier
            .aspectRatio(0.8f)
            .clip(RoundedCornerShape(8.dp))
            .background(AdminColors.Outline.copy(alpha = 0.25f)),
        contentAlignment = Alignment.Center,
    ) {
        val bmp = bitmap
        when {
            bmp != null -> Image(
                bitmap = bmp,
                contentDescription = "Banner guardado",
                modifier = Modifier.matchParentSize(),
                contentScale = ContentScale.Fit,
            )
            failed -> Text("No se pudo cargar la imagen", color = AdminColors.TextMuted, fontSize = 12.sp)
            else -> CircularProgressIndicator(color = AdminColors.Primary)
        }
    }
}

@Composable
private fun DetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun PromotionFormDialog(
    title: String,
    initial: AdminPromotion?,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminPromotionUpsert) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var promoTitle by remember(initial) { mutableStateOf(initial?.title.orEmpty()) }
    var channel by remember(initial) {
        mutableStateOf(initial?.channel ?: org.gymly.model.AdminMarketingChannel.APP)
    }
    var startsAt by remember(initial) { mutableStateOf(initial?.startsAt?.take(10).orEmpty()) }
    var endsAt by remember(initial) { mutableStateOf(initial?.endsAt?.take(10).orEmpty()) }
    var scheduleDate by remember(initial) {
        mutableStateOf(initial?.scheduledAt?.take(10).orEmpty())
    }
    var scheduleTime by remember(initial) {
        mutableStateOf(initial?.scheduledAt?.let { extractHm(it) }.orEmpty().ifBlank { "18:00" })
    }
    var active by remember(initial) { mutableStateOf(initial?.active ?: true) }
    val hostedVideo = initial?.videoUrl?.takeIf { it.contains("/api/marketing/promotions/") }
    var videoUrl by remember(initial) {
        mutableStateOf(initial?.videoUrl?.takeUnless { it.contains("/api/marketing/promotions/") }.orEmpty())
    }
    var bannerName by remember { mutableStateOf<String?>(null) }
    var bannerPayload by remember { mutableStateOf<PickedImagePayload?>(null) }
    val bannerHolder = remember { PickedMediaHolder() }
    var formError by remember { mutableStateOf<String?>(null) }
    var picking by remember { mutableStateOf(false) }
    var bannerPreviewLarge by remember { mutableStateOf(false) }

    Dialog(
        onDismissRequest = { if (!saving) onDismiss() },
        properties = DialogProperties(dismissOnClickOutside = false),
    ) {
        // Celular: el diálogo no supera la pantalla; el título y los botones quedan fijos y solo se desplaza el formulario.
        Column(
            Modifier
                .widthIn(max = 560.dp)
                .fillMaxWidth()
                .fillMaxHeight(0.92f)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
            Spacer(Modifier.height(14.dp))
            Column(
                Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
            ) {
            FormLabel("Canal de marketing *")
            Spacer(Modifier.height(6.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                org.gymly.model.AdminMarketingChannel.entries.forEach { opt ->
                    val selected = channel == opt
                    OutlinedButton(
                        onClick = { channel = opt },
                        enabled = !saving,
                        modifier = Modifier.weight(1f),
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.outlinedButtonColors(
                            containerColor = if (selected) AdminColors.Primary.copy(alpha = 0.12f) else AdminColors.Surface,
                            contentColor = if (selected) AdminColors.Primary else AdminColors.Text,
                        ),
                        border = androidx.compose.foundation.BorderStroke(
                            1.dp,
                            if (selected) AdminColors.Primary else AdminColors.Outline,
                        ),
                    ) {
                        Text(opt.labelEs(), fontSize = 12.sp, fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal)
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            FormLabel("Título *")
            FormField(promoTitle, { promoTitle = it }, "2x1 en plan mensual")
            Spacer(Modifier.height(10.dp))
            FormLabel(if (initial == null) "Banner / flyer o video *" else "Banner / flyer o video")
            // Vista previa de la imagen elegida (solo imágenes; los videos muestran el nombre del archivo).
            val bannerBitmap = remember(bannerPayload) {
                bannerPayload
                    ?.takeIf { it.mimeType.startsWith("image/") }
                    ?.bytes
                    ?.takeIf { it.isNotEmpty() }
                    ?.let { bytes -> runCatching { bytes.decodeToImageBitmap() }.getOrNull() }
            }
            Column(
                Modifier
                    .fillMaxWidth()
                    .heightIn(min = 120.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .border(1.dp, AdminColors.Outline, RoundedCornerShape(12.dp))
                    .padding(12.dp),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                if (bannerBitmap != null) {
                    // Marco 4:5, igual que en el portal: si la imagen no es 4:5, el hueco lo llena una copia borrosa.
                    Box(
                        Modifier
                            .fillMaxWidth(if (bannerPreviewLarge) 1f else 0.6f)
                            .aspectRatio(0.8f)
                            .clip(RoundedCornerShape(8.dp))
                            .background(AdminColors.Outline.copy(alpha = 0.25f)),
                    ) {
                        Image(
                            bitmap = bannerBitmap,
                            contentDescription = null,
                            modifier = Modifier.matchParentSize().blur(24.dp),
                            contentScale = ContentScale.Crop,
                        )
                        Image(
                            bitmap = bannerBitmap,
                            contentDescription = "Vista previa del banner",
                            modifier = Modifier.matchParentSize(),
                            contentScale = ContentScale.Fit,
                        )
                    }
                    TextButton(onClick = { bannerPreviewLarge = !bannerPreviewLarge }) {
                        Text(
                            if (bannerPreviewLarge) "Reducir vista previa" else "Ampliar vista previa",
                            fontSize = 12.sp,
                        )
                    }
                    val bannerRatio = bannerBitmap.width.toFloat() / bannerBitmap.height.toFloat()
                    if (kotlin.math.abs(bannerRatio - 0.8f) > 0.03f) {
                        Text(
                            "Esta imagen no es 4:5. Para que se vea completa en Instagram, WhatsApp y el portal, súbela en vertical (1080×1350).",
                            color = Color(0xFFEF6C00),
                            fontSize = 12.sp,
                        )
                    }
                }
                // Banner ya guardado y sin archivo nuevo: se muestra la imagen almacenada.
                val portadaActual = initial?.portadaImagen()
                if (bannerBitmap == null && bannerPayload == null && hostedVideo == null && portadaActual != null) {
                    StoredBannerPreview(
                        fullUrl = portadaActual.fullUrl,
                        modifier = Modifier.fillMaxWidth(0.6f),
                    )
                }
                FileUploadIconButton(
                    onClick = {
                        formError = null
                        picking = true
                        openPromoMediaPicker { picked ->
                            if (picked == null) {
                                bannerHolder.clear()
                                scope.launch {
                                    picking = false
                                    formError = "No se seleccionó ningún archivo."
                                }
                                return@openPromoMediaPicker
                            }
                            val payload = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType)
                            bannerHolder.set(payload, picked.fileName)
                            scope.launch {
                                picking = false
                                bannerPayload = payload
                                bannerName = picked.fileName
                                formError = null
                            }
                        }
                    },
                    tooltip = if (bannerName != null) "Cambiar archivo" else "Subir imagen o video",
                    enabled = !picking && !saving,
                    tint = AdminColors.Primary,
                )
                Text(
                    bannerName
                        ?: when {
                            hostedVideo != null || initial?.displayType == "VIDEO" -> "Video actual"
                            initial?.slides?.isNotEmpty() == true -> "Imagen actual (${initial.slides.size})"
                            else -> "Imagen 4:5, ideal 1080×1350 px (vertical). Video mp4/webm hasta 40 MB."
                        },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }

            if (channel.includesApp()) {
                Spacer(Modifier.height(14.dp))
                MarketingSectionCard(title = "App (carrusel)", hint = "Se muestra en la app y web móvil entre estas fechas.") {
                    FormLabel("Desde")
                    AdminDateField(
                        value = startsAt,
                        onValueChange = { startsAt = it },
                        placeholder = "AAAA-MM-DD o DD/MM/AAAA",
                        outputFormat = AdminDateOutputFormat.ISO,
                    )
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Hasta")
                    AdminDateField(
                        value = endsAt,
                        onValueChange = { endsAt = it },
                        placeholder = "AAAA-MM-DD o DD/MM/AAAA",
                        outputFormat = AdminDateOutputFormat.ISO,
                    )
                }
            }

            if (channel.includesWhatsApp()) {
                Spacer(Modifier.height(14.dp))
                MarketingSectionCard(
                    title = "WhatsApp (flyer)",
                    hint = "Se envía con el WhatsApp configurado en Ajustes. Indica cuándo programar el envío.",
                ) {
                    FormLabel("Fecha de envío *")
                    AdminDateField(
                        value = scheduleDate,
                        onValueChange = { scheduleDate = it },
                        placeholder = "AAAA-MM-DD o DD/MM/AAAA",
                        outputFormat = AdminDateOutputFormat.ISO,
                    )
                    Spacer(Modifier.height(10.dp))
                    FormLabel("Hora de envío *")
                    OutlinedTextField(
                        value = scheduleTime,
                        onValueChange = { scheduleTime = it.filter { ch -> ch.isDigit() || ch == ':' }.take(5) },
                        modifier = Modifier.fillMaxWidth(),
                        singleLine = true,
                        placeholder = { Text("18:00", color = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    Text(
                        "Formato 24 h (HH:mm). Zona America/Caracas (−04:00).",
                        color = AdminColors.TextMuted,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
            }

            Spacer(Modifier.height(10.dp))
            FormLabel("Video (enlace, opcional)")
            OutlinedTextField(
                value = videoUrl,
                onValueChange = { videoUrl = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = { Text("https://…", color = AdminColors.TextMuted) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            Text(
                "Si subes un video o pegas un enlace, la promoción puede ir sin imagen.",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
            Spacer(Modifier.height(8.dp))
            Row(verticalAlignment = Alignment.CenterVertically) {
                Checkbox(checked = active, onCheckedChange = { active = it })
                Text("Activa", color = AdminColors.Text)
            }
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 12.sp)
            }
            }
            Spacer(Modifier.height(18.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val banner = bannerHolder.payload ?: bannerPayload
                        val startIso = if (channel.includesApp() && startsAt.isNotBlank()) parseFlexibleDateToIso(startsAt) else null
                        val endIso = if (channel.includesApp() && endsAt.isNotBlank()) parseFlexibleDateToIso(endsAt) else null
                        val schedDateIso = if (channel.includesWhatsApp()) parseFlexibleDateToIso(scheduleDate) else null
                        when {
                            promoTitle.isBlank() -> formError = "El título es obligatorio."
                            initial == null && banner == null && videoUrl.isBlank() ->
                                formError = "Sube una imagen, un video o indica el enlace del video."
                            channel.includesApp() && startsAt.isNotBlank() && startIso == null ->
                                formError = "Desde: usa DD/MM/AAAA o AAAA-MM-DD."
                            channel.includesApp() && endsAt.isNotBlank() && endIso == null ->
                                formError = "Hasta: usa DD/MM/AAAA o AAAA-MM-DD."
                            channel.includesApp() && startIso != null && endIso != null && endIso < startIso ->
                                formError = "La fecha Hasta debe ser posterior o igual a Desde."
                            channel.includesWhatsApp() && schedDateIso == null ->
                                formError = "Indica la fecha de envío por WhatsApp."
                            channel.includesWhatsApp() && !isValidHm(scheduleTime) ->
                                formError = "Hora de envío: usa HH:mm (ej. 18:00)."
                            else -> {
                                formError = null
                                val scheduledAt = if (channel.includesWhatsApp() && schedDateIso != null) {
                                    buildScheduledAtIso(schedDateIso, scheduleTime)
                                } else {
                                    null
                                }
                                val video = videoUrl.trim().ifBlank { null }
                                    ?: hostedVideo?.takeIf { banner == null }
                                val uploadedVideo = banner?.mimeType?.startsWith("video/") == true ||
                                    banner?.fileName?.let { name ->
                                        val n = name.lowercase()
                                        n.endsWith(".mp4") || n.endsWith(".webm") || n.endsWith(".mov")
                                    } == true
                                val display = when {
                                    uploadedVideo || (!video.isNullOrBlank() && banner == null && initial?.slides.isNullOrEmpty()) -> "VIDEO"
                                    else -> initial?.displayType ?: "SINGLE"
                                }
                                onSave(
                                    AdminPromotionUpsert(
                                        title = promoTitle.trim(),
                                        channel = channel,
                                        startsAt = if (channel.includesApp()) startIso else null,
                                        endsAt = if (channel.includesApp()) endIso else null,
                                        scheduledAt = scheduledAt,
                                        active = active,
                                        displayType = display,
                                        sortOrder = initial?.sortOrder ?: 0,
                                        details = initial?.details,
                                        banner = banner,
                                        videoUrl = video,
                                    ),
                                )
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar")
                }
            }
        }
    }
}

@Composable
private fun MarketingSectionCard(
    title: String,
    hint: String,
    content: @Composable ColumnScope.() -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Primary.copy(alpha = 0.28f), RoundedCornerShape(12.dp))
            .background(AdminColors.Primary.copy(alpha = 0.05f))
            .padding(14.dp),
    ) {
        Text(title, fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 14.sp)
        Text(hint, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(top = 2.dp, bottom = 10.dp))
        content()
    }
}

private fun promotionSubtitle(p: AdminPromotion): String {
    val parts = mutableListOf(p.channel.labelEs())
    if (p.channel.includesApp()) parts.add("App ${p.dateRangeLabel()}")
    if (p.channel.includesWhatsApp()) parts.add("WA ${p.scheduledLabel()}")
    return parts.joinToString(" · ")
}

private fun extractHm(iso: String): String {
    val t = iso.substringAfter('T', "").take(5)
    return if (isValidHm(t)) t else ""
}

private fun isValidHm(raw: String): Boolean {
    val m = Regex("""^(\d{1,2}):(\d{2})$""").matchEntire(raw.trim()) ?: return false
    val h = m.groupValues[1].toIntOrNull() ?: return false
    val min = m.groupValues[2].toIntOrNull() ?: return false
    return h in 0..23 && min in 0..59
}

private fun buildScheduledAtIso(dateIso: String, timeHm: String): String {
    val parts = timeHm.trim().split(':')
    val hm = "${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}"
    return "${dateIso.take(10)}T${hm}:00-04:00"
}

@Composable
private fun FormLabel(text: String) {
    Text(text, color = AdminColors.TextMuted, fontSize = 12.sp, modifier = Modifier.padding(bottom = 4.dp))
}

@Composable
private fun FormField(value: String, onChange: (String) -> Unit, placeholder: String) {
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text(placeholder, color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
